// SPDX-License-Identifier: AGPL-3.0-or-later

import {SettingsRow} from '@app/features/app/components/dialogs/shared/SettingsRow';
import {SettingsSection} from '@app/features/app/components/dialogs/shared/SettingsSection';
import {capabilityError} from '@app/features/konvo_connections/state/AssistantCapabilities';
import {http} from '@app/features/platform/transport/RestTransport';
import {Button} from '@app/features/ui/button/Button';
import {Trans} from '@lingui/react/macro';
import {useEffect, useState} from 'react';
import styles from './KonvoAssistantSettingsTab.module.css';

interface Report {
	id: string;
	filename: string;
	format: string;
	created_at: string;
}
export function KonvoAssistantReportsSection() {
	const [reports, setReports] = useState<Array<Report>>([]);
	const [error, setError] = useState('');
	const [busy, setBusy] = useState<string | null>(null);
	const refresh = async () => {
		try {
			const response = await http.get<{reports: Array<Report>}>('/konvo/assistant/reports');
			setReports(response.body.reports);
		} catch (e) {
			setError(capabilityError(e, 'Reports unavailable'));
		}
	};
	useEffect(() => {
		void refresh();
	}, []);
	return (
		<SettingsSection
			id="assistant-reports"
			title={<Trans>Reports</Trans>}
			description={
				<Trans>Reports exported by the assistant and your automations are available here for 30 days.</Trans>
			}
			defaultExpanded={false}
			actions={
				<Button variant="ghost" small onClick={() => void refresh()}>
					<Trans>Refresh</Trans>
				</Button>
			}
		>
			{reports.map((r) => (
				<SettingsRow
					key={r.id}
					title={r.filename}
					description={new Date(r.created_at).toLocaleString()}
					control={() => (
						<Button
							variant="secondary"
							small
							submitting={busy === r.id}
							onClick={() => {
								setBusy(r.id);
								setError('');
								void http
									.get<{filename: string; content: string; format: string}>(`/konvo/assistant/reports/${r.id}/download`)
									.then((response) => {
										const file = response.body;
										const blob = new Blob([file.content], {
											type: file.format === 'csv' ? 'text/csv;charset=utf-8' : 'text/plain;charset=utf-8',
										});
										const url = URL.createObjectURL(blob);
										const anchor = document.createElement('a');
										anchor.href = url;
										anchor.download = file.filename;
										document.body.append(anchor);
										anchor.click();
										anchor.remove();
										window.setTimeout(() => URL.revokeObjectURL(url), 30000);
									})
									.catch((e) => setError(capabilityError(e, 'Download unavailable')))
									.finally(() => setBusy(null));
							}}
						>
							<Trans>Download</Trans>
						</Button>
					)}
				/>
			))}
			{reports.length === 0 && (
				<p className={styles.note}>
					<Trans>Ask the assistant to export a report as Markdown, text or CSV.</Trans>
				</p>
			)}
			{error && (
				<p className={styles.note} role="alert">
					{error}
				</p>
			)}
		</SettingsSection>
	);
}
